/** Reusable debounced-merge autosave helper.
 *
 * Queues partial patches, coalesces them into one pending patch, and
 * saves on a debounce timer. Only one save is ever in flight; patches
 * queued while a save is in flight are merged and saved afterwards.
 * `flush()` lets callers force an immediate save (e.g. on navigation
 * away from a frame, or when the tab is hidden). */

import { ApiError } from "./client";

export type DraftState = {
  dirty: boolean;
  saving: boolean;
  error: string;
};

export class Autosave<P extends object> {
  #save: (patch: P) => Promise<void>;
  #delayMs: number;
  #onState?: (state: DraftState) => void;

  #pending: Partial<P> | null = null;
  #timer: number | null = null;
  #inFlight: Promise<void> | null = null;

  #state: DraftState = { dirty: false, saving: false, error: "" };

  constructor(
    save: (patch: P) => Promise<void>,
    opts?: { delayMs?: number; onState?: (state: DraftState) => void },
  ) {
    this.#save = save;
    this.#delayMs = opts?.delayMs ?? 600;
    this.#onState = opts?.onState;
  }

  get state(): DraftState {
    return this.#state;
  }

  queue(patch: Partial<P>): void {
    this.#pending = { ...(this.#pending ?? {}), ...patch };
    this.#setState({ dirty: true });
    this.#restartTimer();
  }

  async flush(): Promise<void> {
    this.#clearTimer();
    // If a save is already running, wait for it, then save whatever is
    // still pending (queue() may have added more work meanwhile).
    if (this.#inFlight) {
      await this.#inFlight.catch(() => undefined);
    }
    if (this.#pending) {
      await this.#runSave();
    }
  }

  dispose(): void {
    this.#clearTimer();
    this.#pending = null;
    this.#inFlight = null;
  }

  #restartTimer(): void {
    this.#clearTimer();
    this.#timer = window.setTimeout(() => {
      this.#timer = null;
      this.#runSave().catch(() => undefined);
    }, this.#delayMs);
  }

  #clearTimer(): void {
    if (this.#timer !== null) {
      window.clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  async #runSave(): Promise<void> {
    // Re-entrant callers (the debounce timer firing mid-save) just
    // ride along with the save that's already running; it re-schedules
    // itself for any newly-pending work once it completes.
    if (this.#inFlight) {
      return this.#inFlight;
    }
    const patch = this.#pending;
    if (!patch) return;
    this.#pending = null;

    const run = (async () => {
      this.#setState({ saving: true, error: "" });
      try {
        await this.#save(patch as P);
        this.#setState({ saving: false, dirty: this.#pending !== null });
      } catch (err) {
        // Keep the failed patch pending (merged with anything queued
        // since) so a later flush/timer retries it.
        this.#pending = { ...patch, ...(this.#pending ?? {}) };
        this.#setState({
          saving: false,
          dirty: true,
          error: errorMessage(err),
        });
      } finally {
        this.#inFlight = null;
      }
      // Drain: if more was queued during the save, schedule it too.
      if (this.#pending && !this.#state.error) {
        this.#restartTimer();
      }
    })();

    this.#inFlight = run;
    return run;
  }

  #setState(patch: Partial<DraftState>): void {
    const next: DraftState = { ...this.#state, ...patch };
    const changed =
      next.dirty !== this.#state.dirty ||
      next.saving !== this.#state.saving ||
      next.error !== this.#state.error;
    this.#state = next;
    if (changed) this.#onState?.(this.#state);
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.detail || err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong while saving.";
}
